import { useCallback, useEffect, useState } from 'react';
import { useUrlState } from './useUrlState';

/**
 * Debounce a fast-changing value (a search box) before it drives filtering or
 * a fetch. The raw value keeps the input responsive; the debounced one is what
 * downstream work should depend on.
 */
export function useDebouncedValue(value, delay = 300) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

/**
 * A remembered choice among a fixed set of values, persisted in localStorage.
 *
 * Deliberately localStorage and not the URL or the database: this is how
 * *you* like to work in this browser, not what you're looking at, so it
 * shouldn't travel in a shared link or follow the account to another device.
 *
 * `allowed` guards against a stale or hand-edited value selecting something
 * the page no longer offers.
 */
export function usePersistedChoice(storageKey, defaultValue, allowed) {
    const [value, setValue] = useState(() => {
        try {
            const stored = window.localStorage.getItem(storageKey);
            return stored && allowed.includes(stored) ? stored : defaultValue;
        } catch {
            return defaultValue; // private mode / storage disabled
        }
    });

    const change = useCallback(
        (next) => {
            if (!allowed.includes(next)) return;
            setValue(next);
            try {
                window.localStorage.setItem(storageKey, next);
            } catch {
                /* preference just won't persist */
            }
        },
        // `allowed` is a literal at every call site; depending on it by
        // identity would rebuild this callback on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [storageKey]
    );

    return [value, change];
}

/**
 * List/grid (or kanban) preference, persisted per page.
 *
 * Keys are namespaced `ll.view.<page>` — the pages that predate this each
 * invented their own ("assetsViewMode", "inboxViewMode", "expensesViewMode").
 */
export function useViewMode(pageKey, defaultMode = 'list', allowed = ['list', 'grid']) {
    return usePersistedChoice(`ll.view.${pageKey}`, defaultMode, allowed);
}

/**
 * The standard list-page control set: search, filter, sort — all in the URL —
 * plus a debounced copy of the search term.
 *
 * Every part is optional. A page with nothing but a search box passes only
 * `defaultSort: null` and ignores the rest; a page with no controls at all
 * shouldn't use this hook.
 *
 *   const { search, setSearch, debouncedSearch,
 *           filter, setFilter, sort, setSort, isFiltered, reset } =
 *     useListControls({ defaultFilter: 'attention', defaultSort: 'date_desc' });
 */
export function useListControls({
    defaultFilter = 'all',
    defaultSort = 'date_desc',
    searchKey = 'q',
    filterKey = 'filter',
    sortKey = 'sort',
    debounceMs = 300,
} = {}) {
    // Typing is continuous → replace, so the back stack isn't flooded.
    const [search, setSearch] = useUrlState(searchKey, '', { mode: 'replace' });
    const [filter, setFilter] = useUrlState(filterKey, defaultFilter);
    // A page without a sort control still calls this; the key must stay a real
    // string (URLSearchParams would coerce null to the literal "null"). Nothing
    // is ever written unless the page calls setSort.
    const [sort, setSort] = useUrlState(sortKey || 'sort', defaultSort);

    const debouncedSearch = useDebouncedValue(search, debounceMs);

    // "Has the user narrowed anything?" — drives which empty state to show:
    // a first-run explainer, or a no-matches message.
    const isFiltered = Boolean(search) || filter !== defaultFilter;

    const reset = useCallback(() => {
        setSearch('');
        setFilter(defaultFilter);
    }, [setSearch, setFilter, defaultFilter]);

    return {
        search,
        setSearch,
        debouncedSearch: debouncedSearch.trim(),
        filter,
        setFilter,
        sort,
        setSort,
        isFiltered,
        reset,
    };
}

export default useListControls;
